import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from "react-i18next";

import {
  Form,
  Space,
  Button,
  Drawer,
  Select
} from '@lockerpm/design';

import {
} from '@ant-design/icons';

import commonComponents from '../../../../../components/common';

import global from '../../../../../config/global';
import projectServices from '../../../../../services/project';
import workspaceServices from '../../../../../services/workspace';

function FormData(props) {
  const { OnboardingTooltip } = commonComponents;
  const {
    visible = false,
    item = null,
    environments = [],
    onClose = () => {},
    onReload = () => {},
  } = props
  const { t } = useTranslation()
  const selectedWorkspace = useSelector((state) => state.workspace.selectedWorkspace)
  const selectedProject = useSelector((state) => state.project.selectedProject)

  const [form] = Form.useForm();
  const [callingAPI, setCallingAPI] = useState(false);
  const [wsMembers, setWsMembers] = useState([]);
  const [projectMembers, setProjectMembers] = useState([]);
  const [searchText, setSearchText] = useState('');

  const usernames = Form.useWatch('usernames', form) || [];
  const role = Form.useWatch('role', form) || null;
  const environmentIds = Form.useWatch('environment_ids', form);

  useEffect(() => {
    if (visible) {
      fetchWsMembers()
      fetchProjectMembers()
    }
  }, [visible])

  const filteredWsMembers = useMemo(() => {
    const members = wsMembers.filter((m) => m.status !== global.constants.WS_MEMBER_STATUS.CREATED)
    if (searchText) {
      return members
        .filter((m) => m.user.username.toLowerCase().includes(searchText.toLowerCase()) ||  m.user.full_name.toLowerCase().includes(searchText.toLowerCase()))
    }
    return members
  }, [searchText, wsMembers])

  useEffect(() => {
    form.setFieldsValue({
      usernames: item ? [item.organization_member?.id] : [],
      role: item ? item.role : global.constants.PROJECT_MEMBER_ROLES[0].value,
      environment_ids: item && item.access_environments ? ['', ...item.access_environments.map((e) => e.environment_id)] : ['']
    })
  }, [visible, item])

  const fetchWsMembers = () => {
    workspaceServices.members(selectedWorkspace?.id, {
      paging: 0,
      is_public_key: true
    }).then((response) => {
      setWsMembers(response);
    }).catch(() => {
      setWsMembers([])
    })
  }

  const fetchProjectMembers = () => {
    projectServices.members(selectedProject.id, { paging: 0 }).then((response) => {
      setProjectMembers(response);
    }).catch(() => {
      setProjectMembers([])
    })
  }

  const getNewMembers = async () => {
    return await Promise.all(wsMembers.filter((m) => usernames.includes(m.id)).map(async (m) => {
      const result = {
        organization_member_id: m.id,
        role: role,
        key: await global.jsCore.cryptoService.generateMemberKey(m.user.public_key, selectedProject)
      }
      if (environmentIds?.length > 0) {
        return {
          ...result,
          environment_ids: environmentIds.filter(Boolean),
        }
      }
      return result
    }))
  }

  const handleSave = async () => {
    setCallingAPI(true);
    if (item) {
      await updateMember();
    } else {
      await addMembers();
    }
    
    setCallingAPI(false);
  }

  const handleClose = () => {
    form.resetFields();
    onClose()
  }

  const addMembers = async () => {
    const members = await getNewMembers();
    await projectServices.create_members(selectedProject.id, members).then(() => {
      global.pushSuccess(t('notification.success.member.added_project'))
      onReload();
      fetchProjectMembers();
      onClose();
    }).catch((error) => {
      global.pushError(error)
    })
  }

  const updateMember = async () => {
    const payload = environmentIds?.length > 0 ? { role, environment_ids: environmentIds.filter(Boolean) } : { role }
    await projectServices.update_member(selectedProject.id, item.id, payload).then(() => {
      global.pushSuccess(t('notification.success.member.updated'))
      onReload();
      onClose();
    }).catch((error) => {
      global.pushError(error)
    })
  }

  return (
    <div className={props.className}>
      <Drawer
        title={item ? t('members.edit') : t('members.new')}
        placement="right"
        onClose={handleClose}
        open={visible}
        footer={
          <Space className='flex items-center justify-end'>
            <Button
              disabled={callingAPI}
              onClick={handleClose}
            >
              {t('button.cancel')}
            </Button>
            <Button
              type="primary"
              disabled={!usernames.length}
              loading={callingAPI}
              onClick={handleSave}
            >
              { item ? t('button.save') : t('button.add') } 
            </Button>
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          labelAlign={'left'}
        >
          <Form.Item
            name={'usernames'}
            label={<div>
              <p className='font-bold'>{t('common.email')}</p>
              <span>{t('member.username_description')}</span>
            </div>}
          >
            <Select
              placeholder={t('placeholder.select')}
              className="w-full"
              disabled={callingAPI || !!item}
              mode='multiple'
              filterOption={false}
              onSearch={(v) => setSearchText(v)}
            >
              {filteredWsMembers.map((m) => (
                <Select.Option
                  value={m.id}
                  key={m.id}
                  disabled={!!projectMembers.find((member) => m.user.user_id === member.organization_member.user.user_id)}
                  title={m.user.username}
                >
                  {m.user.full_name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name={'role'}
            label={<div>
              <div className='flex items-center'>
                <p className='font-bold mr-2'>{t('common.role')}</p>
                <OnboardingTooltip
                  width={300}
                  title={<div>
                    <p>
                      <Trans i18nKey={'onboarding.project_admin'} components={{ b: <b className='font-semibold'/> }}/>
                    </p>
                    <p>
                      <Trans i18nKey={'onboarding.project_regular'} components={{ b: <b className='font-semibold'/> }}/>
                    </p>
                    <p>
                      <Trans i18nKey={'onboarding.project_read_only'} components={{ b: <b className='font-semibold'/> }}/>
                    </p>
                  </div>}
                  placement="top"
                />
              </div>
              <span>{t('member.role_description')}</span>
            </div>}
          >
            <Select
              placeholder={t('placeholder.select')}
              className="w-full"
              disabled={callingAPI}
            >
              {global.constants.PROJECT_MEMBER_ROLES.map((p) => (
                <Select.Option
                  value={p.value}
                  key={p.value}
                >
                  {t(p.label)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          {
            role !== global.constants.PROJECT_MEMBER_ROLE.ADMIN && <Form.Item
              name={'environment_ids'}
              label={<div>
                <p className='font-bold'>{t('member.access_environment')}</p>
                <span>{t('member.access_environment_desc')}</span>
              </div>}
            >
              <Select
                placeholder={t('placeholder.select')}
                className="w-full"
                mode='multiple'
                disabled={callingAPI}
              >
                <Select.Option
                  value={""}
                  disabled
                >
                  <OnboardingTooltip
                    title={t('onboarding.env_all_default')}
                    placement="left"
                    content={
                      <div className='w-full'>
                        {t('common.all_default')}
                      </div>
                    }
                  />
                </Select.Option>
                {environments.map((env) => (
                  <Select.Option
                    value={env.id}
                    key={env.id}
                  >
                    {env.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          }
        </Form>
      </Drawer>
    </div>
  );
}

export default FormData;
